import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useAvailability } from '../contexts/AvailabilityContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isBefore, startOfDay, isToday, addMonths, subMonths,
} from 'date-fns';

export const DatePickerCalendar = ({
  value,
  onChange,
  minDate,
  label,
  testId,
  mode = 'checkin',  // 'checkin' or 'checkout'
  checkInDate = null, // for checkout mode: highlight the range
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    if (value) return startOfMonth(new Date(value));
    if (checkInDate) return startOfMonth(new Date(checkInDate));
    return startOfMonth(new Date());
  });
  const [blockedMessage, setBlockedMessage] = useState('');
  const ref = useRef(null);
  const { isDateBooked } = useAvailability();
  const { t } = useLanguage();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setBlockedMessage('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // When value changes externally, sync view month
  useEffect(() => {
    if (value) setViewMonth(startOfMonth(new Date(value)));
  }, [value]);

  const today = startOfDay(new Date());
  const effectiveMin = minDate ? startOfDay(new Date(minDate)) : today;

  const weekdays = t('availability.weekdays');

  const generateDays = () => {
    const start = startOfMonth(viewMonth);
    const end = endOfMonth(viewMonth);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfWeek = start.getDay();
    const padding = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    return [...Array(padding).fill(null), ...days];
  };

  const handleDayClick = (date) => {
    if (!date) return;
    const d = startOfDay(date);

    if (isBefore(d, effectiveMin)) return;

    if (isDateBooked(date)) {
      const bf = t('bookingForm');
      setBlockedMessage(bf.dateNotAvailable || 'This date is not available. Please select another date.');
      setTimeout(() => setBlockedMessage(''), 3000);
      return;
    }

    setBlockedMessage('');
    onChange(format(date, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const getDayClass = (date) => {
    if (!date) return 'dp-day empty';
    const d = startOfDay(date);
    const classes = ['dp-day'];

    if (isBefore(d, effectiveMin)) classes.push('past');
    else if (isDateBooked(date)) classes.push('booked');
    else classes.push('available');

    if (isToday(date)) classes.push('today');
    if (value && format(date, 'yyyy-MM-dd') === value) classes.push('selected');

    // Highlight range between check-in and selected checkout
    if (mode === 'checkout' && checkInDate && value) {
      const ci = startOfDay(new Date(checkInDate));
      const co = startOfDay(new Date(value));
      if (d > ci && d < co) classes.push('in-range');
    }

    return classes.join(' ');
  };

  const days = generateDays();

  return (
    <div className="dp-wrapper" ref={ref}>
      <button
        type="button"
        className={`dp-trigger ${value ? 'has-value' : ''}`}
        onClick={() => { setIsOpen(!isOpen); setBlockedMessage(''); }}
        data-testid={testId}
      >
        <Calendar className="h-4 w-4" />
        <span>{value ? format(new Date(value), 'dd MMM yyyy') : label}</span>
      </button>

      {isOpen && (
        <div className="dp-dropdown" data-testid={`${testId}-dropdown`}>
          <div className="dp-header">
            <button type="button" className="dp-nav-btn" onClick={() => setViewMonth(subMonths(viewMonth, 1))}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="dp-month-label">{format(viewMonth, 'MMMM yyyy')}</span>
            <button type="button" className="dp-nav-btn" onClick={() => setViewMonth(addMonths(viewMonth, 1))}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="dp-weekdays">
            {Array.isArray(weekdays) && weekdays.map(d => (
              <div key={d} className="dp-weekday">{d}</div>
            ))}
          </div>

          <div className="dp-grid">
            {days.map((date, i) => (
              <button
                key={i}
                type="button"
                className={getDayClass(date)}
                onClick={() => handleDayClick(date)}
                disabled={!date}
              >
                {date ? format(date, 'd') : ''}
              </button>
            ))}
          </div>

          {blockedMessage && (
            <div className="dp-blocked-msg" data-testid="date-blocked-message">
              {blockedMessage}
            </div>
          )}

          <div className="dp-legend">
            <span className="dp-legend-item"><span className="dp-dot available"></span> {t('availability.available')}</span>
            <span className="dp-legend-item"><span className="dp-dot booked"></span> {t('availability.booked')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
